import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Wallet, Clock, Landmark, ScrollText, Calculator } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { tr } from "@/lib/i18n";

const fmt = (n) => `$${(n || 0).toLocaleString("en-CA", { maximumFractionDigits: 0 })}`;

export default function Financials() {
    const { tr } = useLanguage();
    const [data, setData] = useState(null);
    const [dcUnits, setDcUnits] = useState(100);
    const [dcType, setDcType] = useState("single");

    useEffect(() => {
        api.get("/financials").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-40 rounded-[8px] bg-slate-100 dark:bg-white/5 animate-pulse" />)}</div>;

    const DC_RATES = { single: 42500, townhouse: 34800, apartment: 26700 };
    const dcEstimate = dcUnits * DC_RATES[dcType];

    const kpis = [
        { label: tr("Revenue Collected YTD"), value: fmt(data.kpis.revenue_ytd), icon: Wallet, testid: "fin-kpi-revenue", color: "#06D6A0" },
        { label: tr("Outstanding Fees"), value: fmt(data.kpis.outstanding), icon: Clock, testid: "fin-kpi-outstanding", color: "#EF476F" },
        { label: tr("DC Collections YTD"), value: fmt(data.kpis.dc_collected), icon: Landmark, testid: "fin-kpi-dc", color: "#00B4D8" },
        { label: tr("Active Letters of Credit"), value: data.kpis.active_lcs, icon: ScrollText, testid: "fin-kpi-lcs", color: "#FFB703" },
    ];

    return (
        <div data-testid="financials-page" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-heading text-navy dark:text-white">{tr("Financials")}</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{tr("Fees, development charges, receipts and letters of credit")}</p>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {kpis.map((k, i) => (
                    <motion.div key={k.label} data-testid={k.testid} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                        className="bg-card rounded-[18px] border border-white/70 dark:border-white/10 border-l-4 p-5 card-shadow hover:card-shadow-hover hover:-translate-y-0.5 transition-[transform,box-shadow]"
                        style={{ borderLeftColor: k.color }}>
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400">{tr(k.label)}</div>
                            <div className="w-8 h-8 rounded-[10px] flex items-center justify-center" style={{ backgroundColor: `${k.color}1A` }}>
                                <k.icon className="w-4 h-4" style={{ color: k.color }} />
                            </div>
                        </div>
                        <div className="mt-2 font-data font-semibold text-2xl text-navy dark:text-white">{k.value}</div>
                    </motion.div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="lg:col-span-8 bg-card rounded-[8px] border border-slate-200/60 dark:border-white/10 card-shadow p-6">
                    <h2 className="font-heading font-semibold text-navy dark:text-white">{tr("Revenue Trend")}</h2>
                    <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed mt-1 mb-4 max-w-xl" data-testid="fin-chart-narration">
                        {tr("Monthly collections across application fees and development charges. Peaks align with DC installment due dates; sustained gaps between collected and outstanding fees indicate files stalling before registration.")}
                    </p>
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={data.revenue_chart}>
                            <defs>
                                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#00B4D8" stopOpacity={0.35} />
                                    <stop offset="100%" stopColor="#00B4D8" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                            <Area type="monotone" dataKey="revenue" stroke="#00B4D8" strokeWidth={2.5} fill="url(#rev)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-4 bg-navy rounded-[8px] p-6 text-white card-shadow">
                    <div className="flex items-center gap-2 mb-5">
                        <Calculator className="w-4 h-4 text-[#00B4D8]" />
                        <h2 className="font-heading font-semibold">{tr("DC Calculator")}</h2>
                    </div>
                    <label className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">{tr("Unit type")}</label>
                    <select data-testid="dc-type-select" value={dcType} onChange={(e) => setDcType(e.target.value)} className="mt-1.5 w-full rounded-[6px] bg-white/10 border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]">
                        <option value="single" className="text-navy">{tr("Single / Semi-Detached")}</option>
                        <option value="townhouse" className="text-navy">{tr("Townhouse")}</option>
                        <option value="apartment" className="text-navy">{tr("Apartment")}</option>
                    </select>
                    <label className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mt-4 block">{tr("Units")}</label>
                    <input data-testid="dc-units-input" type="number" value={dcUnits} onChange={(e) => setDcUnits(Number(e.target.value) || 0)} className="mt-1.5 w-full rounded-[6px] bg-white/10 border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                    <div className="mt-6 pt-5 border-t border-white/10">
                        <div className="text-xs text-slate-400">{tr("Rate:")} <span className="font-data">{fmt(DC_RATES[dcType])}/unit</span></div>
                        <div className="mt-1.5 font-data font-bold text-3xl text-[#FFB703]" data-testid="dc-estimate">{fmt(dcEstimate)}</div>
                        <div className="text-[11px] text-slate-500 mt-1">{tr("Estimated development charges")}</div>
                    </div>
                </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="lg:col-span-7 bg-card rounded-[8px] border border-slate-200/60 dark:border-white/10 card-shadow overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100 dark:border-white/5">
                        <h2 className="font-heading font-semibold text-navy dark:text-white">{tr("Recent Transactions")}</h2>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100 dark:border-white/5">
                                <th className="px-5 py-3 font-semibold">{tr("File #")}</th>
                                <th className="px-5 py-3 font-semibold">{tr("Description")}</th>
                                <th className="px-5 py-3 font-semibold text-right">{tr("Amount")}</th>
                                <th className="px-5 py-3 font-semibold text-right">{tr("Status")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                            {data.transactions.map((t) => (
                                <tr key={t.id} data-testid={`txn-${t.id.slice(0, 8)}`} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                                    <td className="px-5 py-3 density-pad font-data text-xs text-teal font-medium">{t.file_number}</td>
                                    <td className="px-5 py-3 density-pad density-text text-navy dark:text-slate-200">{t.description}{t.method ? <span className="text-xs text-slate-400"> · {t.method}</span> : null}</td>
                                    <td className="px-5 py-3 density-pad text-right font-data text-xs font-medium text-navy dark:text-white">{fmt(t.amount)}</td>
                                    <td className="px-5 py-3 density-pad text-right">
                                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${t.status === "Paid" ? "bg-[#06D6A0]/12 text-[#05A37E]" : "bg-[#FFB703]/15 text-[#B8860B]"}`}>{t.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="lg:col-span-5 space-y-4">
                    <h2 className="font-heading font-semibold text-navy dark:text-white">{tr("Letters of Credit")}</h2>
                    {data.letters_of_credit.map((lc) => {
                        const pct = Math.round(((lc.amount - lc.balance) / lc.amount) * 100);
                        const days = Math.round((new Date(lc.expiry_date) - Date.now()) / 86400000);
                        return (
                            <div key={lc.id} data-testid={`lc-${lc.id.slice(0, 8)}`} className="bg-card rounded-[8px] border border-slate-200/60 dark:border-white/10 p-5 card-shadow hover:card-shadow-hover transition-[box-shadow]">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-navy dark:text-slate-100 text-sm">{lc.bank}</div>
                                        <div className="font-data text-[11px] text-teal">{lc.file_number}</div>
                                    </div>
                                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${lc.status === "Active" ? "bg-[#06D6A0]/12 text-[#05A37E]" : "bg-[#EF476F]/10 text-[#EF476F]"}`}>
                                        {lc.status === "Expiring Soon" ? `Expires in ${days}d` : lc.status}
                                    </span>
                                </div>
                                <div className="mt-4 flex justify-between text-xs text-slate-500">
                                    <span>Released {pct}%</span>
                                    <span className="font-data">{fmt(lc.balance)} / {fmt(lc.amount)}</span>
                                </div>
                                <div className="mt-1.5 h-2 rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden">
                                    <div className="h-full bg-teal rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </motion.div>
            </div>
        </div>
    );
}
